import React from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@mui/material'
import { HelpOutlineRounded, WarningAmberRounded } from '@mui/icons-material'
import { useAdminTheme } from '../hook/useAdminTheme'
import l from '../page/SubscribersPageV2.module.css'
import s from './ConfirmV2.module.css'

/*
 * "Are you sure?" in the v2 admin design — the v2 pages' PopupAsk, with the
 * same props (header, onYes, onClose, children), so it drops in. A question
 * that starts with a destructive verb (Delete, Block, Withdraw…) gets a red
 * button named after it; `yesLabel` / `danger` say it outright. Enter says
 * yes (the button has the focus), Esc no. The classic pages keep PopupAsk.
 */

const DANGER = /^(delete|remove|block|withdraw|revoke|deactivate|cancel|leave|discard|start a new)\b/i

interface Props {
  onClose: () => void
  onYes?: () => void
  header?: React.ReactNode
  children?: React.ReactNode
  /** the yes button's text — by default the question's first word (Delete, Block…) or "Yes" */
  yesLabel?: string
  noLabel?: string
  danger?: boolean
}

export function ConfirmV2({ onClose, onYes, header, children, yesLabel, noLabel, danger }: Props) {
  const { t } = useTranslation()
  const { theme } = useAdminTheme()
  const text = typeof header === 'string' ? header : ''
  const verb = text.match(DANGER)?.[1]?.toLowerCase()
  const isDanger = danger ?? !!verb
  // "Leave this prompt?" → Discard changes; "Cancel the message?" → Cancel it / Keep; "Delete …?" → Delete
  const yes = yesLabel
    || (verb === 'leave' || verb === 'start a new' ? 'Discard changes'
      : verb === 'cancel' ? 'Cancel it'
        : verb ? verb[0].toUpperCase() + verb.slice(1)
          : t('common.yes'))
  const no = noLabel || (verb === 'cancel' ? 'Keep' : 'Cancel')

  function confirm() {
    onYes?.()
    onClose()
  }

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
      {/* the yes button has the focus — Enter presses it */}
      <div className={`${l.theme} ${s.box}`} data-theme={theme}>
        <div className={s.head}>
          <span className={`${s.icon} ${isDanger ? s.iconDanger : ''}`}>
            {isDanger ? <WarningAmberRounded sx={{ fontSize: 20 }} /> : <HelpOutlineRounded sx={{ fontSize: 20 }} />}
          </span>
          <b className={s.title}>{header || t('common.sure')}</b>
        </div>
        {children && <div className={s.body}>{children}</div>}
        <div className={s.btns}>
          <button type="button" className={s.no} onClick={onClose}>{no}</button>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <button type="button" className={`${s.yes} ${isDanger ? s.yesDanger : ''}`} onClick={confirm} autoFocus>{yes}</button>
        </div>
      </div>
    </Dialog>
  )
}
